// frontend/src/pages/GalleryPage.jsx

import React, { useState, useRef, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Camera } from 'lucide-react';

import gallery1 from '../assets/images/gallery-1.jpg';
import gallery2 from '../assets/images/gallery-2.jpg';
import gallery3 from '../assets/images/gallery-3.jpg';
import gallery4 from '../assets/images/gallery-4.jpg';
import gallery5 from '../assets/images/gallery-5.jpg';
import hero1    from '../assets/images/hero1.jpg';
import hero2    from '../assets/images/hero2.jpg';
import hero3    from '../assets/images/hero3.jpg';
import about    from '../assets/images/about.jpg';

const ALL = 'All';

const IMAGES = [
  { id: 1,  src: gallery1, alt: 'Resort terrace overlooking the valley',  category: 'Views',    featured: true  },
  { id: 2,  src: hero1,    alt: 'Hilltop retreat at golden hour',          category: 'Ambiance', featured: false },
  { id: 3,  src: gallery2, alt: 'Outdoor seating with mountain backdrop',  category: 'Ambiance', featured: false },
  { id: 4,  src: gallery3, alt: 'Signature dining experience',             category: 'Dining',   featured: false },
  { id: 5,  src: hero2,    alt: 'Panoramic city view from the hilltop',    category: 'Views',    featured: true  },
  { id: 6,  src: gallery4, alt: 'Garden lounge at dusk',                   category: 'Ambiance', featured: false },
  { id: 7,  src: gallery5, alt: 'Candlelit table setting',                 category: 'Dining',   featured: false },
  { id: 8,  src: hero3,    alt: 'Aerial view of the resort grounds',       category: 'Views',    featured: false },
  { id: 9,  src: about,    alt: "The founders' original retreat, 2016",    category: 'Ambiance', featured: false },
];

const CATEGORIES = [ALL, 'Views', 'Ambiance', 'Dining'];

const GalleryPage = () => {
  const [activeCategory, setActiveCategory] = useState(ALL);
  const [lightbox, setLightbox]             = useState(null);
  const touchStartX = useRef(null);

  const filtered = activeCategory === ALL
    ? IMAGES
    : IMAGES.filter(img => img.category === activeCategory);

  const openLightbox  = (i) => setLightbox(i);
  const closeLightbox = () => setLightbox(null);

  const prev = useCallback(() => {
    setLightbox(i => (i - 1 + filtered.length) % filtered.length);
  }, [filtered.length]);

  const next = useCallback(() => {
    setLightbox(i => (i + 1) % filtered.length);
  }, [filtered.length]);

  React.useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape')     closeLightbox();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, prev, next]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd   = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) dx < 0 ? next() : prev();
    touchStartX.current = null;
  };

  return (
    <div className="min-h-screen hp2s" style={{ background: '#0A0A0A', color: '#E2D0B0' }}>

      {/* Hero */}
      <div className="relative h-72 sm:h-96 overflow-hidden flex items-end">
        <img
          src={hero1}
          alt="Resort view"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.3)' }}
        />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 70% 50%, rgba(196,162,90,0.12) 0%, transparent 70%)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #0A0A0A 0%, transparent 60%)' }} />
        <div className="relative z-10 w-full max-w-5xl mx-auto px-6 pb-10 sm:pb-14">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px w-6" style={{ background: '#C4A25A' }} />
            <span style={{ color: '#C4A25A', fontSize: '0.68rem', letterSpacing: '0.3em' }} className="uppercase font-semibold">Gallery</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl" style={{ color: '#E2D0B0', fontWeight: 300 }}>
            Glimpses of <em style={{ color: '#C4A25A', fontStyle: 'italic' }}>the hilltop</em>
          </h1>
          <p className="mt-2 text-sm sm:text-base" style={{ color: '#6B6B6B' }}>
            {IMAGES.length} photographs from Swat Capital Resort
          </p>
        </div>
      </div>

      {/* Category Filter */}
      <div className="sticky top-0 z-30" style={{ background: 'rgba(13,13,13,0.92)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(196,162,90,0.1)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex gap-1.5 overflow-x-auto py-3" style={{ scrollbarWidth: 'none' }}>
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className="whitespace-nowrap px-4 py-1.5 text-sm font-medium transition-all"
              style={{
                borderRadius: '2px',
                border: activeCategory === cat ? '1px solid #C4A25A' : '1px solid rgba(196,162,90,0.2)',
                background: activeCategory === cat ? 'rgba(196,162,90,0.12)' : 'transparent',
                color: activeCategory === cat ? '#C4A25A' : '#6B6B6B',
              }}
            >
              {cat}
              {cat !== ALL && (
                <span className="ml-1.5 text-xs opacity-60">
                  {IMAGES.filter(i => i.category === cat).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Masonry Grid */}
      <style>{`
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-auto-rows: 130px;
        }
        @media (min-width: 640px) {
          .gallery-grid { grid-template-columns: repeat(3, 1fr); grid-auto-rows: 180px; }
        }
      `}</style>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 pb-20">
        <div className="gallery-grid gap-2 sm:gap-3">
          {filtered.map((img, idx) => {
            const isFirst = idx === 0 && img.featured;
            return (
              <div
                key={img.id}
                onClick={() => openLightbox(idx)}
                className="group relative overflow-hidden cursor-pointer"
                style={{
                  gridRow:   isFirst ? 'span 2' : 'span 1',
                  gridColumn: isFirst ? 'span 2' : 'span 1',
                  border: '1px solid rgba(196,162,90,0.1)',
                  borderRadius: '2px',
                }}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {/* hover overlay */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end"
                  style={{ background: 'linear-gradient(to top, rgba(13,13,13,0.88) 0%, rgba(13,13,13,0.2) 55%, transparent 100%)' }}
                >
                  <div className="p-3 sm:p-4">
                    <span
                      className="block uppercase mb-1"
                      style={{ color: '#C4A25A', fontSize: '0.6rem', letterSpacing: '0.2em' }}
                    >
                      {img.category}
                    </span>
                    <span className="block text-sm font-light" style={{ color: '#E2D0B0' }}>{img.alt}</span>
                  </div>
                </div>
                {/* category badge */}
                <div
                  className="absolute top-2 left-2 px-2 py-0.5 opacity-70 group-hover:opacity-0 transition-opacity"
                  style={{
                    background: 'rgba(13,13,13,0.75)',
                    border: '1px solid rgba(196,162,90,0.2)',
                    borderRadius: '2px',
                    color: '#C4A25A',
                    fontSize: '0.58rem',
                    letterSpacing: '0.15em',
                  }}
                >
                  {img.category.toUpperCase()}
                </div>
              </div>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-24">
            <Camera className="w-10 h-10 mx-auto mb-3" style={{ color: 'rgba(196,162,90,0.3)' }} />
            <p style={{ color: '#6B6B6B' }}>No photos in this category yet.</p>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.97)', backdropFilter: 'blur(8px)' }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {/* Close */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 p-2"
            style={{ color: '#6B6B6B' }}
          >
            <X className="w-7 h-7" />
          </button>

          {/* Counter */}
          <div
            className="absolute top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 text-sm z-10"
            style={{
              background: 'rgba(13,13,13,0.8)',
              border: '1px solid rgba(196,162,90,0.2)',
              borderRadius: '2px',
              color: '#6B6B6B',
            }}
          >
            <span style={{ color: '#C4A25A' }}>{lightbox + 1}</span>
            <span className="mx-1">/</span>
            {filtered.length}
          </div>

          {/* Prev */}
          <button
            onClick={prev}
            className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-10 p-3"
            style={{
              background: 'rgba(13,13,13,0.7)',
              border: '1px solid rgba(196,162,90,0.2)',
              borderRadius: '2px',
              color: '#E2D0B0',
            }}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          {/* Image + caption */}
          <div className="flex flex-col items-center px-16 sm:px-24 w-full max-w-5xl">
            <img
              src={filtered[lightbox].src}
              alt={filtered[lightbox].alt}
              className="max-h-[72vh] w-auto max-w-full object-contain"
              style={{ borderRadius: '2px' }}
              draggable={false}
            />
            <div className="mt-4 text-center">
              <span
                className="block uppercase mb-1"
                style={{ color: '#C4A25A', fontSize: '0.62rem', letterSpacing: '0.2em' }}
              >
                {filtered[lightbox].category}
              </span>
              <span className="text-sm font-light" style={{ color: '#C8B48E' }}>
                {filtered[lightbox].alt}
              </span>
            </div>
          </div>

          {/* Next */}
          <button
            onClick={next}
            className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-10 p-3"
            style={{
              background: 'rgba(13,13,13,0.7)',
              border: '1px solid rgba(196,162,90,0.2)',
              borderRadius: '2px',
              color: '#E2D0B0',
            }}
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Thumbnail strip */}
          <div
            className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 overflow-x-auto px-3 py-2"
            style={{
              background: 'rgba(13,13,13,0.8)',
              border: '1px solid rgba(196,162,90,0.15)',
              borderRadius: '2px',
              maxWidth: '90vw',
              scrollbarWidth: 'none',
            }}
          >
            {filtered.map((img, idx) => (
              <button
                key={img.id}
                onClick={() => setLightbox(idx)}
                className="flex-shrink-0 transition-all"
                style={{
                  width: 36,
                  height: 36,
                  overflow: 'hidden',
                  borderRadius: '2px',
                  border: idx === lightbox ? '1.5px solid #C4A25A' : '1.5px solid transparent',
                  opacity: idx === lightbox ? 1 : 0.4,
                }}
              >
                <img src={img.src} alt="" className="w-full h-full object-cover" draggable={false} />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
